/**
 * Beside `releaseStages`, a catalog item keeps the stages that need a lab
 * order and the master lab category the order is for:
 * `labStages: [{ stage, categoryId }]`. The form edits one value per stage
 * row ("" = no lab order), so these convert between the two shapes.
 */

export type LabStage = { stage: string; categoryId: string };

const clean = (value: unknown) => String(value ?? "").trim();

function entries(labStages: unknown): LabStage[] {
  return (Array.isArray(labStages) ? labStages : [])
    .map((e: any) => ({ stage: clean(e?.stage), categoryId: clean(e?.categoryId?._id ?? e?.categoryId) }))
    .filter((e) => e.stage && e.categoryId);
}

/** One category id per stage row ("" when the stage needs no lab order). */
export function labCategoriesFor(stages: unknown[], labStages: unknown): string[] {
  const byStage = new Map(entries(labStages).map((e) => [e.stage, e.categoryId]));
  return (Array.isArray(stages) ? stages : []).map((s) => byStage.get(clean(s)) || "");
}

/** What to send: rows with a name and a category, in row order, once per name. */
export function labStagesPayload(stages: string[], categories: string[]): LabStage[] {
  const seen = new Set<string>();
  const out: LabStage[] = [];
  stages.forEach((s, i) => {
    const stage = clean(s);
    const categoryId = clean(categories[i]);
    if (!stage || !categoryId || seen.has(stage)) return;
    seen.add(stage);
    out.push({ stage, categoryId });
  });
  return out;
}

/** The lab category a stage asks an order for, or "". */
export function stageLabCategory(item: { labStages?: unknown } | null | undefined, stage: unknown): string {
  const name = clean(stage);
  return (name && entries(item?.labStages).find((e) => e.stage === name)?.categoryId) || "";
}
